import React, { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { CreateReminderRequest, Reminder } from '@/api/mcp-reminder/interfaces';
import ReminderFormModal, { type ReminderFormInitialValues } from '@/components/reminders/ReminderFormModal';

export interface ReminderCreateSession {
  initialValues?: ReminderFormInitialValues;
  context?: CreateReminderRequest['context'];
  onSuccess?: (reminder: Reminder) => void | Promise<void>;
  onClose?: () => void;
}

interface ReminderContextValue {
  openCreate: (session?: ReminderCreateSession) => void;
  closeCreate: () => void;
}

const ReminderContext = createContext<ReminderContextValue | null>(null);

export function ReminderProvider({ children }: { children: ReactNode }) {
  const [createSession, setCreateSession] = useState<ReminderCreateSession | null>(null);

  const closeCreate = useCallback(() => {
    setCreateSession((current) => {
      current?.onClose?.();
      return null;
    });
  }, []);

  const openCreate = useCallback((session?: ReminderCreateSession) => {
    setCreateSession(session ?? {});
  }, []);

  const handleCreateSuccess = useCallback(async (reminder: Reminder) => {
    await createSession?.onSuccess?.(reminder);
    closeCreate();
  }, [closeCreate, createSession]);

  const value = useMemo<ReminderContextValue>(() => ({
    openCreate,
    closeCreate
  }), [openCreate, closeCreate]);

  return (
    <ReminderContext.Provider value={value}>
      {children}
      <ReminderFormModal
        open={createSession != null}
        onClose={closeCreate}
        initialValues={createSession?.initialValues}
        context={createSession?.context}
        onSuccess={handleCreateSuccess}
      />
    </ReminderContext.Provider>
  );
}

export function useReminder(): ReminderContextValue {
  const context = useContext(ReminderContext);
  if (!context) {
    throw new Error('useReminder must be used within ReminderProvider');
  }
  return context;
}
